import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  className?: string;
  position?: 'top' | 'bottom';
}

export function Tooltip({ content, children, className, position = 'top' }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, 500); // 500ms delay before showing
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isBottom = position === 'bottom';

  return (
    <div
      ref={containerRef}
      className={cn('relative inline-block', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div
          className="absolute z-[var(--z-overlay)] text-sm text-primary-foreground bg-primary rounded shadow-lg pointer-events-none whitespace-pre-wrap break-words"
          style={{
            [isBottom ? 'top' : 'bottom']: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            [isBottom ? 'marginTop' : 'marginBottom']: '0.5rem',
            padding: '0.5rem 1rem',
            minWidth: '16rem',
            maxWidth: '36rem',
            lineHeight: '1.4',
          }}
        >
          {content}
          <div
            className="absolute left-1/2 transform -translate-x-1/2"
            style={{
              [isBottom ? 'bottom' : 'top']: '100%',
              [isBottom ? 'marginTop' : 'marginBottom']: '-0.25rem',
              borderLeft: '0.375rem solid transparent',
              borderRight: '0.375rem solid transparent',
              [isBottom ? 'borderBottom' : 'borderTop']: '0.375rem solid rgb(var(--primary))',
            }}
          />
        </div>
      )}
    </div>
  );
}

